import { useReducer, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { ReportState } from './types';
import { reportsReducer, reportsInitialState, ReportsContext } from '@reports/presentation/contexts';

export type ReportsProviderProps = {
  children: ReactNode;
  initialState?: Partial<ReportState>;
};

/**
 * Provider component for Reports domain state management
 *
 * Wraps components that need access to Reports domain state.
 * Uses useReducer for state management with optimized re-renders.
 *
 * @param {ReportsProviderProps} props - Provider props
 * @param {ReactNode} props.children - Child components
 * @param {Partial<ReportState>} [props.initialState] - Optional initial state override
 * @returns {React.ReactElement} Provider component
 *
 * @example
 * <ReportsProvider>
 *   <ReportsPage />
 * </ReportsProvider>
 *
 * @example
 * <ReportsProvider initialState={{ currentReport: someReport }}>
 *   <ReportsPage />
 * </ReportsProvider>
 */
export const ReportsProvider = ({ children, initialState }: ReportsProviderProps) => {
  const [state, dispatch] = useReducer(
    reportsReducer,
    initialState ? { ...reportsInitialState, ...initialState } : reportsInitialState,
  );

  const contextValue = useMemo(() => ({ state, dispatch }), [state, dispatch]);

  return <ReportsContext.Provider value={contextValue}>{children}</ReportsContext.Provider>;
};
